/**
 * Import Wizard Component
 * 
 * Multi-step wizard for importing Excel/CSV data:
 * 1. Upload file
 * 2. Map fields
 * 3. Validate data
 * 4. Import
 * 5. Results
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  Box,
  Button,
  Typography,
  Alert
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  SwapHoriz as MapIcon,
  CheckCircle as ValidateIcon,
  Download as ImportIcon,
  Done as DoneIcon
} from '@mui/icons-material';

import FileUploadStep from './steps/FileUploadStep';
import FieldMappingStep from './steps/FieldMappingStep';
import ValidationStep from './steps/ValidationStep';
import ImportStep from './steps/ImportStep';
import ResultsStep from './steps/ResultsStep';

interface ImportWizardProps {
  open: boolean;
  onClose: () => void;
  entityType: 'customer' | 'supplier' | 'invoice' | 'expense' | 'employee';
  onImportComplete?: (result: any) => void;
}

const steps = [
  { label: 'Upload File', icon: <UploadIcon /> },
  { label: 'Map Fields', icon: <MapIcon /> },
  { label: 'Validate Data', icon: <ValidateIcon /> },
  { label: 'Import', icon: <ImportIcon /> },
  { label: 'Complete', icon: <DoneIcon /> }
];

const ImportWizard: React.FC<ImportWizardProps> = ({
  open,
  onClose,
  entityType,
  onImportComplete
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<any>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [validationResult, setValidationResult] = useState<any>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
    setError(null);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSessionId(null);
    setFileInfo(null);
    setMappings({});
    setValidationResult(null);
    setImportResult(null);
    setError(null);
  };

  const handleClose = () => {
    if (importResult && onImportComplete) {
      onImportComplete(importResult);
    }
    handleReset();
    onClose();
  };

  const handleFileUploaded = (session: any) => {
    setSessionId(session.session_id);
    setFileInfo(session);
    handleNext();
  };

  const handleFieldsMapped = (newMappings: Record<string, string>) => {
    setMappings(newMappings);
    handleNext();
  };

  const handleValidationComplete = (result: any) => {
    setValidationResult(result);
    handleNext();
  };

  const handleImportComplete = (result: any) => {
    setImportResult(result);
    handleNext();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <FileUploadStep
            entityType={entityType}
            onFileUploaded={handleFileUploaded}
            onError={handleError}
          />
        );
      case 1:
        return (
          <FieldMappingStep
            sessionId={sessionId!}
            entityType={entityType}
            fileInfo={fileInfo}
            onMappingComplete={handleFieldsMapped}
            onError={handleError}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <ValidationStep
            sessionId={sessionId!}
            mappings={mappings}
            onValidationComplete={handleValidationComplete}
            onError={handleError}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <ImportStep
            sessionId={sessionId!}
            mappings={mappings}
            validationResult={validationResult}
            onImportComplete={handleImportComplete}
            onError={handleError}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <ResultsStep
            importResult={importResult}
            onClose={handleClose}
            onStartNew={handleReset}
          />
        );
      default:
        return null;
    }
  };

  const getEntityTypeLabel = () => {
    const labels = {
      customer: 'Customers',
      supplier: 'Suppliers',
      invoice: 'Invoices',
      expense: 'Expenses',
      employee: 'Employees'
    };
    return labels[entityType] || entityType;
  };

  return (
    <Dialog
      open={open}
      onClose={activeStep === 4 ? handleClose : undefined}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={activeStep > 0 && activeStep < 4}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">
            Import {getEntityTypeLabel()}
          </Typography>
          {activeStep < 4 && (
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Stepper */}
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: index === activeStep ? 'primary.main' : 
                                index < activeStep ? 'success.main' : 'grey.300',
                        color: 'white'
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        <Box sx={{ minHeight: 400 }}>
          {getStepContent(activeStep)}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ImportWizard;

